/** Pure helpers for medical partner list rows (no React). */

/**
 * Partner lifecycle is **Live** (API `active` / `Active`): typically when they are verified and may appear on public surfaces.
 * Draft/pending partners can still configure TC, MT, clinics, etc. in admin — **visibility** should be enforced by API/site rules, not by locking these toggles here.
 */
export const isActiveStatus = (item) => item?.status === "active" || item?.status === "Active";

export const isPendingStatus = (item) => item?.status === "pending" || item?.status === "Pending";

export const isSuspendedStatus = (item) =>
  item?.status === "Suspend" ||
  item?.status === "Suspended" ||
  item?.status === "inactive";

export const isDraftStatus = (item) => item?.status === "draft";

/**
 * Workflow status string for the grid (legacy Suspend/Suspended → inactive).
 */
export function partnerWorkflowStatus(item) {
  const s = String(item?.status ?? "").trim();
  const sl = s.toLowerCase();
  if (sl === "active") return "active";
  if (sl === "pending") return "pending";
  if (sl === "draft") return "draft";
  if (sl === "inactive" || sl === "suspend" || sl === "suspended") return "inactive";
  return sl || "pending";
}

/**
 * User may be chosen as primary when creating a new MedicalPartner (no row yet).
 * Prevents one directory account from owning two partner orgs (or a legacy ContactPartner link).
 */
export function filterUserEligibleAsNewMedicalPartnerPrimary(u) {
  if (!u?._id) return false;
  if (u.medicalPartnerId || u.contactPartnerId) return false;
  return true;
}

/** User can be set as this partner's primary account (directory search). */
export function filterUserAssignableAsPrimaryPartner(u, partner) {
  if (!u?._id || !partner?._id) return false;
  const pid = String(partner._id);
  const mp = u.medicalPartnerId;
  const mpId = mp && typeof mp === "object" && mp._id ? String(mp._id) : mp != null ? String(mp) : "";
  if (mpId && mpId !== pid) return false;
  return true;
}

/**
 * Primary login exists but the user record looks disabled/suspended (API shapes vary).
 * Edge case: partner workflow active while login cannot sign in.
 */
export function partnerPrimaryUserAccountSuspended(item) {
  const u = item?.userId;
  if (!u || typeof u !== "object") return false;
  if (u.isActive === false || u.active === false) return true;
  const st = String(u.status ?? u.accountStatus ?? "").trim().toLowerCase();
  if (["inactive", "suspended", "blocked", "disabled", "deleted"].includes(st)) return true;
  return false;
}

/** Primary user / role / link consistency for list UX (was “Primary login” column). */
export function partnerListNeedsAccountAttention(item) {
  if (!item?.userId) return false;
  const active = isActiveStatus(item);
  const role = item.userId?.role;
  const roleOk = role === "medical_partner" || role === "admin";
  const mp = item.userId?.medicalPartnerId;
  const mpStr = mp ? (typeof mp === "object" && mp._id ? String(mp._id) : String(mp)) : "";
  const linkedElsewhere = Boolean(mpStr && mpStr !== String(item._id));
  const missingLink = active && !item.userId?.medicalPartnerId && !item.userId?.contactPartnerId;
  const contactWithoutMp = Boolean(item.userId?.contactPartnerId && !item.userId?.medicalPartnerId);
  return !roleOk || linkedElsewhere || missingLink || contactWithoutMp;
}
